<template>
    <div class="container-fluid">
        <div class="row mb-4">
            <div class="col">
                <h1 class="h5">
                    <BootstrapIcon class="me-3 align-middle" color="fill-gray-700 text-gray-700" name="people" :size="20" />
                    <span class="align-middle">{{$t('management_console.members.title')}}</span>
                </h1>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <div class="bg-white shadow-sm rounded px-3 py-2 mb-3 d-flex justify-content-between align-items-center">
                    <div class="flex-grow-1">
                        <span>{{$t('management_console.members.count', { count: paginator.count, limit: quota.member_limit })}}</span>
                        <Tooltip class="ms-2" :value="$t('tooltips.quota_details')" icon="info-circle" />
                    </div>
                    <button v-if="paginator.count < quota.member_limit && (user.role === 'admin' || user.permissions.api.includes('POST_/v1/members'))" @click="request_create_member()" type="button" class="btn btn-sm btn-primary bg-gradient px-4">{{$t('management_console.members.add')}}</button>
                </div>
            </div>
        </div>

        <div v-if="paginator.count > 0" class="row">
            <div class="col">
                <div class="rounded shadow-sm bg-white px-3 py-3 table-responsive">
                    <table class="table table-sm table-hover">
                        <thead class="fw-normal small text-gray-700">
                            <tr class="">
                                <th scope="col" class="border-0 pb-3">{{$t('management_console.common.username')}}</th>
                                <th scope="col" class="border-0 pb-3">{{$t('management_console.common.role')}}</th>
                                <th scope="col" class="border-0 pb-3">{{$t('management_console.members.sessions')}}</th>
                                <th scope="col" class="border-0 pb-3">
                                    {{$t('member_notifications.title')}}
                                    <Tooltip class="ms-1" :value="$t('member_notifications.status_help')" icon="info-circle" />
                                </th>
                                <th scope="col" class="border-0 pb-3">{{$t('management_console.members.assigned_workplaces')}}</th>
                                <th scope="col" class="border-0 pb-3">PIN</th>
                                <th scope="col" class="border-0 pb-3 text-end">{{$t('management_console.common.actions')}}</th>
                            </tr>
                            <tr>
                                <th>
                                    <input v-model="filter.username" type="text" class="form-control" :placeholder="$t('management_console.members.filter_username')">
                                </th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody class="text-gray-700  border-top-0">
                            <tr v-for="(member, index) in members" :key="index" class="align-middle">
                                <td>
                                    <div class="fw-bolder">
                                        <HighlightText :search="filter.username" :value="member.username" />
                                    </div>
                                    <div class="text-nowrap small">{{$filters.PrettyDate(member.created_at)}}</div>
                                </td>
                                <td>
                                    <span
                                    class="console-role-chip"
                                    :class="`console-role-chip--${member.role}`">
                                        <span class="console-role-chip__icon" aria-hidden="true">
                                            <BootstrapIcon
                                            :name="member.role === 'delivery' ? 'truck' : 'person-badge'"
                                            :size="14" />
                                        </span>
                                        {{$t(`management_console.member_modal.role_${member.role}`)}}
                                    </span>
                                </td>
                                <td>
                                    <button
                                    v-if="member.sessions.length > 0"
                                    type="button"
                                    class="btn btn-link p-0 align-baseline"
                                    @click="request_member_sessions(member)">
                                        {{session_count_label(member.sessions.length)}}
                                    </button>
                                    <div v-else>
                                        {{$t('management_console.members.no_sessions')}}
                                    </div>
                                </td>
                                <td>
                                    <span v-if="member.role !== 'delivery'" class="small text-gray-500">
                                        {{$t('member_notifications.unavailable')}}
                                    </span>
                                    <div v-else-if="has_notifications(member)" class="small">
                                        <div class="d-flex align-items-center fw-bolder text-green-500 text-nowrap">
                                            <BootstrapIcon class="me-2" color="fill-teal-500 text-green-500" name="bell-fill" :size="16" />
                                            {{$t('member_notifications.enabled')}}
                                        </div>
                                        <div class="d-flex flex-wrap gap-1 mt-2">
                                            <span
                                            v-for="device in notification_device_groups(member)"
                                            :key="device.platform"
                                            class="badge rounded-pill border bg-gray-200 text-gray-800 fw-normal">
                                                {{notification_platform_label(device.platform)}}<span v-if="device.count > 1"> ×{{device.count}}</span>
                                            </span>
                                        </div>
                                        <div v-if="latest_notification_confirmation(member)" class="mt-2 text-gray-600 text-nowrap">
                                            <Tooltip class="me-1" :value="$t('member_notifications.last_seen_help')" icon="info-circle" />
                                            {{$t('member_notifications.last_confirmed')}}:
                                            {{$filters.PrettyDate(latest_notification_confirmation(member))}}
                                        </div>
                                    </div>
                                    <div v-else class="d-flex align-items-center small text-gray-500 text-nowrap">
                                        <BootstrapIcon class="me-2" color="fill-gray-500 text-gray-500" name="bell" :size="16" />
                                        {{$t('member_notifications.not_enabled')}}
                                    </div>
                                </td>
                                <td>
                                    <span v-if="Object.keys(member.locations).length === 0">{{$t('management_console.common.all')}}</span>
                                    <div v-else>
                                        <div v-for="(name, id) in member.locations" :key="id">{{name}}</div>
                                    </div>
                                </td>
                                <td>
                                    <Clipboard class="me-2" :value="member.pin.toString()" /> <span class="fw-bolder">{{member.pin}}</span>
                                </td>
                                <td class="text-nowrap text-end">
                                    <button
                                    v-if="Array.isArray(member.sessions) && member.sessions.length > 0 && (user.role === 'admin' || user.permissions.api.includes('DELETE_/v1/members/sessions/:member_id'))"
                                    type="button"
                                    @click="request_confirm_delete_all_sessions(member)"
                                    class="btn btn-sm btn-outline-danger mx-1">
                                        {{$t('member_sessions.remove_all')}}
                                    </button>
                                    <button v-if="(user.role === 'admin' || user.permissions.api.includes('PUT_/v1/members/:member_id/pin'))" type="button" @click="generate_pin(member)" class="btn btn-sm btn-success bg-gradient mx-1">
                                        {{$t('management_console.members.generate_pin')}}
                                    </button>
                                    <button v-if="(user.role === 'admin' || user.permissions.api.includes('PUT_/v1/members/:member_id'))" type="button" @click="request_edit_member(member)" class="btn btn-sm btn-secondary bg-gradient mx-1">
                                        {{$t('controls.edit')}}
                                    </button>
                                    <button v-if="(user.role === 'admin' || user.permissions.api.includes('DELETE_/v1/members/:member_id'))" type="button" @click="request_confirm_delete_member(member)" class="btn btn-sm btn-danger">
                                        {{$t('management_console.common.delete')}}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div v-else class="row">
            <div class="col">
                <EmptyResponse :description="$t('management_console.members.empty')" />
            </div>
        </div>

        <Paginator v-if="paginator.count>0" :tag="'members'" :page="paginator.page" :limit="paginator.limit" :records="paginator.count" class="pt-3" />

        <ModalManageStoreMember :member="selected_member" />
        <ModalConfirmDeleteMember :member="selected_member" />
        <ModalConfirmDeleteMemberSessions :member="selected_member" />
        <ModalMemberSessions :member="selected_member" />
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import Tooltip from '@/common/directive/Tooltip'
import HighlightText from '@/common/directive/HighlightText.vue'
import Clipboard from '@/common/directive/Clipboard'
import InventoryMemberStore from '@/common/store/inventory_member.js'
import EmptyResponse from '@/common/directive/EmptyResponse.vue'
import Paginator from '@/common/directive/Paginator.vue'
import ModalManageStoreMember from '@/common/modal/console/modal-manage_store_member.vue'
import ModalConfirmDeleteMember from '@/common/modal/console/modal-confirm_delete_member.vue'
import ModalConfirmDeleteMemberSessions from '@/common/modal/console/modal-confirm_delete_member_sessions.vue'
import ModalMemberSessions from '@/common/modal/console/modal-member_sessions.vue'

export default {
    components: {
        Tooltip,
        Clipboard,
        Paginator,
        BootstrapIcon,
        HighlightText,
        // ModalManageKey,
        // ModalConfirmDeleteKey,
        // BootstrapIcon,
        EmptyResponse,
        ModalManageStoreMember,
        ModalConfirmDeleteMember,
        ModalConfirmDeleteMemberSessions,
        ModalMemberSessions
    },
    data: function () {
        return {
            paginate_timeout: 0,
            selected_member: {},
            filter: {
                username: '',
                name: ''
            }
        }
    },
    computed: {
        user: function () {
            return this.$store.state.user
        },
        quota: function () {
            return this.$store.state.quota
        },
        members: () => InventoryMemberStore.state.members,
        paginator: () => InventoryMemberStore.state.paginator,
        is_authorized: function () {
            return this.$store.state.is_authorized
        }
    },
    watch: {
        filter: {
            handler () {
                this.set_page(1)
            },
            deep: true
        }
    },
    created: async function () {
        if (!this.is_authorized) {
            this.$router.replace('/login')
        }

        this.$bus.$off('Paginator/members/set_page')
        this.$bus.$on('Paginator/members/set_page', (value) => {
            this.set_page(value)
        })
        await this.paginate()
    },
    methods: {
        set_page: async function (page) {
            InventoryMemberStore.commit('SET_PAGE', page)
            window.scroll({ top: 0 })
            this.paginate()
        },
        paginate: async function () {
            const self = this
            clearTimeout(this.paginate_timeout)
            // self.$bus.$emit('ListingLoader/show')
            this.paginate_timeout = setTimeout(async function () {
                const query = {}
                if (self.filter.username && self.filter.username.length >= 1) {
                    query.username = self.filter.username
                }
                if (self.filter.name && self.filter.name.length >= 1) {
                    query.name = self.filter.name
                }
                await InventoryMemberStore.dispatch('PAGINATE', {
                    page: self.paginator.page,
                    ...query
                })
            }, 200)
        },
        request_create_member: function () {
            this.selected_member = {}
            this.$bus.$emit('Modal/show/manage-store-member')
        },
        request_edit_member: function (member) {
            this.selected_member = member
            this.$bus.$emit('Modal/show/manage-store-member')
        },
        request_confirm_delete_member: function (member) {
            this.selected_member = member
            this.$bus.$emit('Modal/show/confirm-delete-member')
        },
        request_confirm_delete_all_sessions: function (member) {
            this.selected_member = member
            this.$bus.$emit('Modal/show/confirm-delete-member-sessions')
        },
        request_member_sessions: function (member) {
            this.selected_member = member
            this.$bus.$emit('Modal/show/member-sessions')
        },
        session_count_label: function (count) {
            const key = count === 1 ? 'session_count_one' : 'session_count_many'
            return this.$t(`management_console.members.${key}`, { count })
        },
        has_notifications: function (member) {
            return Boolean(
                member &&
                    member.notifications &&
                    member.notifications.enabled === true &&
                    Array.isArray(member.notifications.devices) &&
                    member.notifications.devices.length > 0
            )
        },
        notification_device_groups: function (member) {
            if (!this.has_notifications(member)) {
                return []
            }

            const groups = member.notifications.devices.reduce(function (result, device) {
                const platform = device && ['iphone', 'android'].includes(device.platform) ? device.platform : 'other'
                if (!result[platform]) {
                    result[platform] = {
                        platform,
                        count: 0
                    }
                }
                result[platform].count += 1
                return result
            }, {})

            return ['iphone', 'android', 'other']
                .filter(platform => typeof groups[platform] !== 'undefined')
                .map(platform => groups[platform])
        },
        notification_platform_label: function (platform) {
            const key = ['iphone', 'android'].includes(platform) ? platform : 'other'
            return this.$t(`member_notifications.platform_${key}`)
        },
        latest_notification_confirmation: function (member) {
            if (!this.has_notifications(member)) {
                return null
            }

            const dates = member.notifications.devices
                .map(device => device && (device.last_seen_at || device.enabled_at))
                .filter(value => typeof value === 'string' && Number.isFinite(Date.parse(value)))
                .sort(function (a, b) {
                    return Date.parse(b) - Date.parse(a)
                })

            return dates[0] || null
        },
        generate_pin: async function (member) {
            const response = await InventoryMemberStore.dispatch('GENERATE_PIN', {
                id: member.id
            })

            if (response.status === 0) {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.operation_success'), msg: this.$t('notifications.operation_success_description'), level: 'success' })
            } else {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            }
        }
    }
}
</script>
